import axios from 'axios';
import { notifyApiError } from '../apiErrorNotifications';

const ExpressApi = axios.create({
    baseURL: `${env.VITE_API_EXPRESS}`,
    withCredentials: true,
});

ExpressApi.interceptors.response.use(
    (response) => response,
    (error) => {
        notifyApiError(error);
        return Promise.reject(error);
    },
);

const API_CACHE_TTL = 24 * 60 * 60 * 1000;

const getCached = async (key, url, config) => {
    const cached = localStorage.getItem(key);
    if (cached) {
        try {
            const { data, expiresAt } = JSON.parse(cached);
            if (expiresAt > Date.now()) {
                return { data };
            }
        } catch {
            localStorage.removeItem(key);
        }
    }

    const response = await ExpressApi.get(url, config);
    localStorage.setItem(
        key,
        JSON.stringify({
            data: response.data,
            expiresAt: Date.now() + API_CACHE_TTL,
        }),
    );
    return { data: response.data };
};

const clearCached = (...keys) => {
    keys.forEach((key) => localStorage.removeItem(key));
};

const addUser = async (data) => {
    try {
        const response = await ExpressApi.post('/addUser', { ...data });
        return response.data;
    } catch (error) {
        return {
            status: 'error',
            msg: error?.response?.data?.msg || error.message || 'Błąd sieci',
        };
    }
};

const getPages = async () => {
    try {
        return await getCached('API_PAGES', '/pages');
    } catch (error) {
        return { data: null, error: error?.response?.data || error.message || 'Błąd sieci' };
    }
};

const changePages = async (data) => {
    try {
        const response = await ExpressApi.put('/pages', { data });
        clearCached(
            'API_PAGES',
            'API_NAVIGATION',
            'API_NAVBAR',
            'MAIN_PAGES',
            'MAIN_PARAMS',
            'ADMIN_PAGES',
            'ADMIN_PARAMS',
            'ROUTES_EXPIRE',
        );
        return response.data;
    } catch (error) {
        console.error('httpLogout error:', error);
        return {
            status: 'error',
            msg: error?.response?.data?.msg || error.message || 'Błąd sieci',
        };
    }
};

const getPage = async (category, name) => {
    try {
        const key = `API_PAGE:${category}:${name}`;
        return await getCached(key, '/page', { params: { category, page: name } });
    } catch (error) {
        return { data: null, error: error?.response?.data || error.message || 'Błąd sieci' };
    }
};

const getCostumeCatalog = async (params, config = {}) => {
    try {
        const response = await ExpressApi.get('/costume-catalog', { params, ...config });
        return { data: response.data };
    } catch (error) {
        return {
            data: null,
            error: error?.response?.data?.error || error.message || 'Błąd pobierania katalogu strojów',
        };
    }
};

const getCustomPage = async (path) => {
    try {
        const response = await ExpressApi.get('/custom-page', { params: { path } });
        return { data: response.data };
    } catch (error) {
        return {
            data: null,
            error: error?.response?.data || error.message || 'Błąd pobierania treści',
        };
    }
};

const httpLogin = async (email, password) => {
    try {
        const response = await ExpressApi.post('/login', {
            email,
            password,
        });
        return response.data;
    } catch (error) {
        return {
            status: 'error',
            msg: error?.response?.data?.msg || error.message || 'Błąd logowania',
        };
    }
};

const httpRefresh = async () => {
    try {
        const response = await ExpressApi.post('/refresh');
        return response.data;
    } catch {
        return { status: 'error', accessToken: null };
    }
};

const httpCheckAuth = async () => {
    try {
        const response = await ExpressApi.post('/checkAuth');
        return response.data;
    } catch {
        return { status: 'error', auth: false };
    }
};

const httpLogout = async () => {
    try {
        const response = await ExpressApi.post('/logout');
        return response.data;
    } catch (error) {
        return {
            status: 'error',
            msg: error?.response?.data?.msg || error.message || 'Błąd wylogowania',
        };
    }
};

const getNavbar = async () => {
    try {
        return await getCached('API_NAVBAR', '/navbar');
    } catch (error) {
        return {
            data: null,
            error: error?.response?.data || error.message || 'Błąd pobierania nawigacji',
        };
    }
};

const getArticle = async (title) => {
    try {
        return await getCached(`API_ARTICLE:${title}`, '/article', { params: { title } });
    } catch (error) {
        return {
            data: null,
            error: error?.response?.data || error.message || 'Błąd pobierania artykułu',
        };
    }
};

const getArticles = async () => {
    try {
        return await getCached('API_ARTICLES', '/articles');
    } catch (error) {
        return {
            data: [],
            error: error?.response?.data || error.message || 'Błąd pobierania artykułów',
        };
    }
};

const getSiteContent = async () => {
    try {
        const response = await ExpressApi.get('/site-content');
        return { data: response.data?.data || response.data || null };
    } catch (error) {
        return {
            data: null,
            error: error?.response?.data || error.message || 'Błąd pobierania treści strony',
        };
    }
};

const saveSiteContent = async (data) => {
    try {
        const response = await ExpressApi.put('/site-content', { data });
        return response.data;
    } catch (error) {
        return {
            status: 'error',
            msg: error?.response?.data?.msg || error.message || 'Błąd zapisu treści strony',
        };
    }
};

const createOrUpdateArticle = async (title, content, oldTitle = '') => {
    try {
        const response = await ExpressApi.put('/article', { title, content, oldTitle });
        clearCached('API_ARTICLES', `API_ARTICLE:${title}`, `API_ARTICLE:${oldTitle}`);
        return response.data;
    } catch (error) {
        return {
            status: 'error',
            msg: error?.response?.data?.msg || error.message || 'Błąd zapisu artykułu',
        };
    }
};

const deleteArticle = async (title) => {
    try {
        const response = await ExpressApi.delete('/article', { data: { title } });
        clearCached('API_ARTICLES', `API_ARTICLE:${title}`);
        return response.data;
    } catch (error) {
        return {
            status: 'error',
            msg: error?.response?.data?.msg || error.message || 'Błąd usuwania artykułu',
        };
    }
};

const getNavigation = async () => {
    try {
        return await getCached('API_NAVIGATION', '/navigation');
    } catch (error) {
        return {
            data: null,
            error: error?.response?.data || error.message || 'Błąd pobierania nawigacji',
        };
    }
};

const getSubpages = async () => {
    try {
        const response = await ExpressApi.get('/subpages');
        return { data: response.data };
    } catch (error) {
        return {
            data: [],
            error: error?.response?.data || error.message || 'Błąd pobierania subpage',
        };
    }
};

const getCustomRoutes = async () => {
    try {
        const response = await ExpressApi.get('/custom-routes');
        return { data: response.data };
    } catch (error) {
        return {
            data: [],
            error: error?.response?.data || error.message || 'Błąd pobierania własnych ścieżek',
        };
    }
};

const updateSubpageContentPath = async (id, contentPath) => {
    try {
        const response = await ExpressApi.put('/subpages', { id, contentPath });
        clearCached(
            'API_NAVIGATION',
            'API_NAVBAR',
            'MAIN_PAGES',
            'MAIN_PARAMS',
            'ADMIN_PAGES',
            'ADMIN_PARAMS',
            'ROUTES_EXPIRE',
        );
        return response.data;
    } catch (error) {
        return {
            status: 'error',
            msg: error?.response?.data?.msg || error.message || 'Błąd aktualizacji subpage',
        };
    }
};

const saveSubpageNavigation = async (pages, customRoutes, removedPageNames = []) => {
    try {
        const response = await ExpressApi.put('/subpages', {
            pages,
            customRoutes,
            removedPageNames,
        });
        if (response.data?.status === 'success') {
            clearCached(
                'API_NAVIGATION',
                'API_NAVBAR',
                'MAIN_PAGES',
                'MAIN_PARAMS',
                'ADMIN_PAGES',
                'ADMIN_PARAMS',
                'ROUTES_EXPIRE',
            );
            window.dispatchEvent(new Event('anikino:navigation-updated'));
        }
        return response.data;
    } catch (error) {
        return {
            status: 'error',
            msg: error?.response?.data?.msg || error.message || 'Błąd zapisu nawigacji',
        };
    }
};

const sendForm = async (formData) => {
    try {
        const response = await ExpressApi.post('/form', formData);
        return response.data;
    } catch (error) {
        return {
            status: 'error',
            msg: error?.response?.data?.msg || error.message || 'Błąd tworzenia folderu',
        };
    }
};

const getDrivePayload = async (path) => {
    try {
        const response = await ExpressApi.get('/drive', { params: { path } });
        return response.data;
    } catch (error) {
        return {
            status: 'error',
            msg: error?.response?.data?.msg || error.message || 'Błąd dostępu do dysku',
        };
    }
};

const getDriveFilePaths = async () => {
    try {
        const response = await ExpressApi.get('/drive/files');
        return response.data;
    } catch (error) {
        return {
            status: 'error',
            msg: error?.response?.data?.msg || error.message || 'Błąd pobierania ścieżek plików',
        };
    }
};

const downloadDriveFile = async (path) => {
    try {
        const response = await ExpressApi.get('/drive/download', {
            params: { path },
            responseType: 'blob',
        });
        return response.data;
    } catch (error) {
        return {
            status: 'error',
            msg: error?.response?.data?.msg || error.message || 'Błąd pobierania pliku',
        };
    }
};

const deleteDriveItem = async (path) => {
    try {
        const response = await ExpressApi.delete('/drive', {
            data: { path },
        });
        return response.data;
    } catch (error) {
        return {
            status: 'error',
            msg: error?.response?.data?.msg || error.message || 'Błąd usuwania pliku',
        };
    }
};

const createDriveFolder = async (path, name) => {
    try {
        const response = await ExpressApi.post('/drive/folder', { params: { path, name } });
        return response.data;
    } catch (error) {
        return {
            status: 'error',
            msg: error?.response?.data?.msg || error.message || 'Błąd tworzenia folderu',
        };
    }
};

const LoadDriveFile = async (formData) => {
    try {
        const response = await ExpressApi.post('/drive', formData, {});
        return response.data;
    } catch (error) {
        return {
            status: 'error',
            msg: error?.response?.data?.msg || error.message || 'Błąd przesyłu pliku',
        };
    }
};

export {
    addUser,
    getPages,
    changePages,
    getPage,
    getCostumeCatalog,
    getCustomPage,
    httpLogin,
    httpRefresh,
    httpCheckAuth,
    httpLogout,
    getNavbar,
    getArticle,
    getArticles,
    getSiteContent,
    saveSiteContent,
    getNavigation,
    getSubpages,
    getCustomRoutes,
    createOrUpdateArticle,
    updateSubpageContentPath,
    saveSubpageNavigation,
    deleteArticle,
    sendForm,
    getDrivePayload,
    getDriveFilePaths,
    downloadDriveFile,
    deleteDriveItem,
    createDriveFolder,
    LoadDriveFile,
};
export default ExpressApi;
